import { useState, useEffect, useMemo } from 'react'
import api from '../../api/axiosInstance'
import Swal from 'sweetalert2'
import {
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  alpha,
} from '@mui/material'
import { Add, Search, Build, Close, Cancel, Refresh } from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import MonitorStatus from '../../components/Animate/monitorStatus'

const ACCENT = '#10b981'

// Bir monitörün bakım durumu üç halden biri:
// active   -> bakım penceresi şu an çalışıyor
// planned  -> pencere kayıtlı ama henüz başlamadı
// none     -> hiç bakım kaydı yok / iptal edilmiş / süresi dolmuş
//
// `status` boolean tek başına yetmiyor: bir bakım oluşturulur oluşturulmaz
// status=true oluyor (pencere gelecekte olsa bile) ve pencere bitene kadar
// öyle kalıyor. Bu yüzden gerçek durumu startTime/endTime ile hesaplıyoruz.
const getMaintenanceState = (monitor) => {
  const m = monitor.maintenance
  // status=false -> hiç bakım yok, iptal edilmiş veya süresi dolmuş
  if (!m || !m.status || !m.startTime || !m.endTime) return 'none'
  const now = Date.now()
  const start = new Date(m.startTime).getTime()
  const end = new Date(m.endTime).getTime()
  if (now >= end) return 'none' // pencere bitti (worker henüz kapatmamış olabilir)
  if (now >= start) return 'active' // pencere şu an çalışıyor -> Bakımda
  return 'planned' // henüz başlamadı -> Planlandı
}

const MAINTENANCE_LABELS = {
  active: { label: 'Bakımda', color: '#6366f1' },
  planned: { label: 'Planlandı', color: '#f59e0b' },
  none: { label: 'Bakım yok', color: '#64748b' },
}

const formatWindow = (value) =>
  value ? dayjs(value).format('DD MMM YYYY HH:mm') : '—'

// Dialog içindeki alanlar koyu zeminde okunur kalsın diye ortak stil
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: alpha('#fff', 0.03),
    borderRadius: 2,
    color: '#fff',
    '& fieldset': { borderColor: alpha(ACCENT, 0.2) },
    '&:hover fieldset': { borderColor: alpha(ACCENT, 0.3) },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: alpha('#fff', 0.6) },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiSvgIcon-root': { color: alpha(ACCENT, 0.7) },
}

function StatsCard({ title, value, color, index }) {
  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      sx={{
        bgcolor: alpha('#fff', 0.03),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 2.5,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(color, 0.4),
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: alpha('#fff', 0.7),
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, color, lineHeight: 1 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  )
}

export default function MaintanancePage() {
  const navigate = useNavigate()

  const [monitors, setMonitors] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedMonitorId, setSelectedMonitorId] = useState('')
  const [startDateTime, setStartDateTime] = useState(dayjs().add(5, 'minute'))
  const [endDateTime, setEndDateTime] = useState(dayjs().add(65, 'minute'))

  const fetchMonitors = async () => {
    try {
      setLoading(true)
      const response = await api.get('monitors/maintenance')
      setMonitors(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      Swal.fire({
        title: 'Hata',
        text: 'Monitörler yüklenirken bir hata oluştu',
        icon: 'error',
      })
      setMonitors([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMonitors()
    const interval = setInterval(fetchMonitors, 60000)
    return () => clearInterval(interval)
  }, [])

  const filteredMonitors = useMemo(() => {
    const term = searchQuery.toLowerCase().trim()
    if (!term) return monitors
    return monitors.filter(
      (monitor) =>
        monitor.name?.toLowerCase().includes(term) ||
        monitor.host?.toLowerCase().includes(term)
    )
  }, [monitors, searchQuery])

  const stats = useMemo(() => {
    const active = monitors.filter((m) => getMaintenanceState(m) === 'active').length
    const planned = monitors.filter((m) => getMaintenanceState(m) === 'planned').length
    return [
      { title: 'Toplam Monitör', value: monitors.length, color: ACCENT },
      { title: 'Bakımda', value: active, color: '#6366f1' },
      { title: 'Planlandı', value: planned, color: '#f59e0b' },
      {
        title: 'Bakım Yok',
        value: monitors.length - active - planned,
        color: '#64748b',
      },
    ]
  }, [monitors])

  // Zaten aktif bir bakım kaydı (planlı veya çalışan, status=true) olan monitöre
  // yenisi eklenemez — backend bu durumda 400 dönüyor, seçeneği baştan
  // listeden çıkarıyoruz.
  const selectableMonitors = useMemo(
    () => monitors.filter((m) => !m.maintenance?.status),
    [monitors]
  )

  const handleOpenDialog = () => {
    setSelectedMonitorId(selectableMonitors[0]?.id ?? '')
    setStartDateTime(dayjs().add(5, 'minute'))
    setEndDateTime(dayjs().add(65, 'minute'))
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!selectedMonitorId) {
      Swal.fire({ title: 'Uyarı', text: 'Lütfen bir monitör seçin', icon: 'warning' })
      return
    }
    if (!startDateTime || !endDateTime) {
      Swal.fire({
        title: 'Uyarı',
        text: 'Lütfen başlangıç ve bitiş zamanını girin',
        icon: 'warning',
      })
      return
    }

    const startDate = startDateTime.second(0).millisecond(0).toDate()
    const endDate = endDateTime.second(0).millisecond(0).toDate()

    if (endDate <= startDate) {
      Swal.fire({
        title: 'Uyarı',
        text: 'Bitiş zamanı başlangıçtan sonra olmalı',
        icon: 'warning',
      })
      return
    }
    if (endDate < new Date()) {
      Swal.fire({ title: 'Uyarı', text: 'Bitiş zamanı geçmişte olamaz', icon: 'warning' })
      return
    }

    try {
      setSaving(true)
      await api.post(`monitors/maintenance/${selectedMonitorId}`, {
        startTime: startDate,
        endTime: endDate,
      })
      setDialogOpen(false)
      Swal.fire({
        title: 'Başarılı',
        text: 'Bakım planı kaydedildi',
        icon: 'success',
      })
      fetchMonitors()
    } catch (error) {
      Swal.fire({
        title: 'Hata',
        text:
          error?.response?.data?.message ||
          'Bakım planı kaydedilirken bir hata oluştu',
        icon: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleCancelMaintenance = async (monitorId) => {
    const confirm = await Swal.fire({
      title: 'Bakım iptal edilsin mi?',
      text: 'Monitör tekrar izlemeye alınacak.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Evet, iptal et',
      cancelButtonText: 'Vazgeç',
    })
    if (!confirm.isConfirmed) return

    try {
      await api.patch(`monitors/maintenance/${monitorId}`)
      Swal.fire({
        title: 'Başarılı',
        text: 'Bakım modu iptal edildi',
        icon: 'success',
      })
      fetchMonitors()
    } catch (error) {
      Swal.fire({
        title: 'Hata',
        text:
          error?.response?.data?.message ||
          'Bakım modu iptal edilirken bir hata oluştu',
        icon: 'error',
      })
    }
  }

  const columns = [
    {
      field: 'status',
      headerName: 'Durum',
      width: 90,
      disableColumnMenu: true,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <MonitorStatus
            status={params.value}
            iconSize={12}
            sx={{ width: 24, height: 24, animeWidth: 15, animeHeight: 15 }}
          />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Monitör',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            py: 0.5,
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>
          <Typography
            sx={{
              color: alpha('#fff', 0.5),
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.row.host || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'maintenanceState',
      headerName: 'Bakım',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const { label, color } = MAINTENANCE_LABELS[params.value]
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Chip
              label={label}
              size="small"
              sx={{
                height: 22,
                bgcolor: alpha(color, 0.15),
                color,
                fontSize: '0.7rem',
                fontWeight: 700,
                border: `1px solid ${alpha(color, 0.3)}`,
              }}
            />
          </Box>
        )
      },
    },
    {
      field: 'startTime',
      headerName: 'Başlangıç',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.8rem' }}>
            {formatWindow(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'endTime',
      headerName: 'Bitiş',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.8rem' }}>
            {formatWindow(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      disableColumnMenu: true,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.maintenanceState === 'active' ||
        params.row.maintenanceState === 'planned' ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Tooltip title="Bakımı iptal et">
              <IconButton
                size="small"
                onClick={() => handleCancelMaintenance(params.row.id)}
                sx={{
                  color: '#ef4444',
                  '&:hover': { bgcolor: alpha('#ef4444', 0.1) },
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
    },
  ]

  const rows = filteredMonitors.map((monitor) => ({
    id: monitor.id,
    name: monitor.name,
    host: monitor.host,
    status: monitor.status,
    maintenanceState: getMaintenanceState(monitor),
    startTime: monitor.maintenance?.startTime ?? null,
    endTime: monitor.maintenance?.endTime ?? null,
  }))

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
            Bakım Yönetimi
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: alpha('#fff', 0.6) }}>
            Planlı bakım pencerelerinizi yönetin
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={stat.title}>
              <StatsCard {...stat} index={index} />
            </Grid>
          ))}
        </Grid>

        {/* Main Content */}
        <Paper
          sx={{
            bgcolor: alpha('#fff', 0.03),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(ACCENT, 0.15)}`,
            borderRadius: 2.5,
            p: 3,
          }}
        >
          {/* Toolbar */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Bakım Planları
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Yenile">
                  <IconButton
                    onClick={fetchMonitors}
                    sx={{
                      color: ACCENT,
                      border: `1px solid ${alpha(ACCENT, 0.2)}`,
                      borderRadius: 2,
                      '&:hover': { bgcolor: alpha(ACCENT, 0.1) },
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenDialog}
                  disabled={selectableMonitors.length === 0}
                  sx={{
                    bgcolor: ACCENT,
                    color: '#000',
                    fontWeight: 600,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#059669' },
                    '&.Mui-disabled': {
                      bgcolor: alpha(ACCENT, 0.2),
                      color: alpha('#fff', 0.4),
                    },
                  }}
                >
                  Bakım Ekle
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: alpha(ACCENT, 0.15) }} />

            <TextField
              placeholder="Monitör adı veya host ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: alpha(ACCENT, 0.6) }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha('#fff', 0.03),
                  borderRadius: 2,
                  color: '#fff',
                  height: 40,
                  '& fieldset': { borderColor: alpha(ACCENT, 0.2) },
                  '&:hover fieldset': { borderColor: alpha(ACCENT, 0.3) },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
          </Box>

          {/* Empty state: hiç monitör yoksa bakım planlanacak bir şey de yok */}
          {!loading && monitors.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                gap: 2,
              }}
            >
              <Build sx={{ fontSize: 48, color: alpha(ACCENT, 0.4) }} />
              <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                Bakım planlanacak monitör yok
              </Typography>
              <Typography
                sx={{ color: alpha('#fff', 0.6), fontSize: '0.875rem', textAlign: 'center' }}
              >
                Bakım penceresi tanımlayabilmek için önce bir monitör oluşturmalısınız.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/user/monitors/new/monitor')}
                sx={{
                  mt: 1,
                  bgcolor: ACCENT,
                  color: '#000',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#059669' },
                }}
              >
                Monitör Oluştur
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                height: 600,
                '& .MuiDataGrid-root': { border: 'none', color: '#fff' },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${alpha(ACCENT, 0.1)}`,
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(ACCENT, 0.05),
                  borderBottom: `1px solid ${alpha(ACCENT, 0.2)}`,
                },
                '& .MuiDataGrid-columnHeader': { color: ACCENT, fontWeight: 700 },
                '& .MuiDataGrid-row:hover': { bgcolor: alpha(ACCENT, 0.05) },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: `1px solid ${alpha(ACCENT, 0.1)}`,
                },
                '& .MuiTablePagination-root': { color: '#fff' },
                '& .MuiTablePagination-selectIcon': { color: ACCENT },
                '& .MuiTablePagination-actions button': { color: ACCENT },
                '& .MuiDataGrid-overlay': {
                  bgcolor: 'transparent',
                  color: alpha('#fff', 0.6),
                },
              }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                disableRowSelectionOnClick
                disableColumnFilter
                sx={{
                  '& .MuiDataGrid-virtualScroller': {
                    '&::-webkit-scrollbar': { width: 6, height: 6 },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: alpha('#fff', 0.05),
                      borderRadius: 1,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: alpha(ACCENT, 0.3),
                      borderRadius: 1,
                      '&:hover': { bgcolor: alpha(ACCENT, 0.5) },
                    },
                  },
                }}
              />
            </Box>
          )}
        </Paper>

        {/* Bakım Ekle dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#0f172a',
              backgroundImage: 'none',
              border: `1px solid ${alpha(ACCENT, 0.2)}`,
              borderRadius: 2.5,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            Bakım Ekle
            <IconButton
              onClick={() => setDialogOpen(false)}
              sx={{ color: alpha('#fff', 0.6) }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <Divider sx={{ borderColor: alpha(ACCENT, 0.15) }} />

          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <TextField
                select
                fullWidth
                label="Monitör"
                value={selectedMonitorId}
                onChange={(e) => setSelectedMonitorId(e.target.value)}
                sx={fieldSx}
                InputLabelProps={{ sx: { color: alpha('#fff', 0.6) } }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        bgcolor: '#0f172a',
                        border: `1px solid ${alpha(ACCENT, 0.2)}`,
                        '& .MuiMenuItem-root': { color: '#fff' },
                        '& .MuiMenuItem-root:hover': {
                          bgcolor: alpha(ACCENT, 0.1),
                        },
                      },
                    },
                  },
                }}
              >
                {selectableMonitors.map((monitor) => (
                  <MenuItem key={monitor.id} value={monitor.id}>
                    {monitor.name} {monitor.host ? `— ${monitor.host}` : ''}
                  </MenuItem>
                ))}
              </TextField>

              <DateTimePicker
                label="Başlangıç"
                value={startDateTime}
                onChange={setStartDateTime}
                ampm={false}
                minDateTime={dayjs()}
                slotProps={{ textField: { fullWidth: true, sx: fieldSx } }}
              />

              <DateTimePicker
                label="Bitiş"
                value={endDateTime}
                onChange={setEndDateTime}
                ampm={false}
                minDateTime={startDateTime || dayjs()}
                slotProps={{ textField: { fullWidth: true, sx: fieldSx } }}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{
                color: alpha('#fff', 0.7),
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Vazgeç
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={
                saving ? <CircularProgress size={16} sx={{ color: '#000' }} /> : <Build />
              }
              sx={{
                bgcolor: ACCENT,
                color: '#000',
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { bgcolor: '#059669' },
                '&.Mui-disabled': {
                  bgcolor: alpha(ACCENT, 0.2),
                  color: alpha('#fff', 0.4),
                },
              }}
            >
              Kaydet
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}
